import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import { incidentsAPI } from '../../services/api';
import { USER_ROLES, SUCCESS_MESSAGES, INCIDENT_STATUS } from '../../utils/constants';
import { FiPlus, FiSearch } from 'react-icons/fi';
import { SkeletonTable } from '../../design-system';
import IncidentForm from '../../components/incidents/IncidentForm';
import IncidentTable from '../../components/incidents/IncidentTable';
import ConfirmDialog from '../../components/common/ConfirmDialog';

const IncidentsManagement = () => {
  const { user } = useAuth();
  const { isMobile, addNotification, hasFeature } = useApp();
  const [incidents, setIncidents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showIncidentForm, setShowIncidentForm] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilters, setStatusFilters] = useState([]); // Array para múltiples estados

  useEffect(() => {
    loadIncidents();
  }, []);

  const loadIncidents = async () => {
    try {
      setIsLoading(true);
      const incidentsData = await incidentsAPI.getAll();
      setIncidents(incidentsData);
    } catch (error) {
      console.error('Error loading incidents:', error);
      addNotification({
        type: 'error',
        message: 'Error al cargar las incidencias'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const canCreate = () => {
    return user?.role === USER_ROLES.CLEANING_STAFF ||
           user?.role === USER_ROLES.RECEPTIONIST ||
           user?.role === USER_ROLES.ESTABLISHMENT_ADMIN;
  };

  const handleCreateIncident = () => {
    setSelectedIncident(null);
    setShowIncidentForm(true);
  };

  const handleEditIncident = (incidentToEdit) => {
    setSelectedIncident(incidentToEdit);
    setShowIncidentForm(true);
  };

  const handleDeleteIncident = (incidentToDelete) => {
    setDeleteConfirm(incidentToDelete);
  };

  const handleCloseIncident = async (incidentId) => {
    try {
      const updatedIncident = await incidentsAPI.close(incidentId);
      setIncidents(prevIncidents =>
        prevIncidents.map(incident =>
          incident.id === incidentId ? updatedIncident : incident
        )
      );

      addNotification({
        type: 'success',
        message: SUCCESS_MESSAGES.INCIDENT_CLOSED
      });
    } catch (error) {
      console.error('Error closing incident:', error);
      addNotification({
        type: 'error',
        message: 'Error al cerrar la incidencia'
      });
    }
  };

  const handleReopenIncident = async (incidentId) => {
    try {
      const updatedIncident = await incidentsAPI.reopen(incidentId);
      setIncidents(prevIncidents =>
        prevIncidents.map(incident =>
          incident.id === incidentId ? updatedIncident : incident
        )
      );

      addNotification({
        type: 'success',
        message: SUCCESS_MESSAGES.INCIDENT_REOPENED
      });
    } catch (error) {
      console.error('Error reopening incident:', error);
      addNotification({
        type: 'error',
        message: 'Error al reabrir la incidencia'
      });
    }
  };

  const confirmDeleteIncident = async () => {
    try {
      await incidentsAPI.delete(deleteConfirm.id);
      setIncidents(prevIncidents => prevIncidents.filter(i => i.id !== deleteConfirm.id));
      setDeleteConfirm(null);

      addNotification({
        type: 'success',
        message: SUCCESS_MESSAGES.INCIDENT_DELETED
      });
    } catch (error) {
      console.error('Error deleting incident:', error);
      addNotification({
        type: 'error',
        message: 'Error al eliminar la incidencia'
      });
    }
  };

  const handleIncidentSaved = async (incidentData) => {
    try {
      if (selectedIncident) {
        // Update existing incident
        const updatedIncident = await incidentsAPI.update(selectedIncident.id, incidentData);
        setIncidents(prevIncidents =>
          prevIncidents.map(incident =>
            incident.id === selectedIncident.id ? updatedIncident : incident
          )
        );

        addNotification({
          type: 'success',
          message: SUCCESS_MESSAGES.INCIDENT_UPDATED
        });
      } else {
        // Create new incident
        const newIncident = await incidentsAPI.create(incidentData);
        setIncidents(prevIncidents => [newIncident, ...prevIncidents]);

        addNotification({
          type: 'success',
          message: SUCCESS_MESSAGES.INCIDENT_CREATED
        });
      }

      setShowIncidentForm(false);
      setSelectedIncident(null);
    } catch (error) {
      console.error('Error saving incident:', error);
      addNotification({
        type: 'error',
        message: selectedIncident ? 'Error al actualizar la incidencia' : 'Error al crear la incidencia'
      });
      throw error; // Re-throw to prevent form from closing
    }
  };

  const toggleStatusFilter = (status) => {
    setStatusFilters(prev =>
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  // Filter incidents based on search and filters
  const filteredIncidents = incidents.filter(incident => {
    const matchesSearch = searchTerm === '' ||
      incident.room_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      incident.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (incident.creator_name && incident.creator_name.toLowerCase().includes(searchTerm.toLowerCase()));

    // Filtro por estado - si no hay filtros seleccionados, mostrar todos
    let matchesStatus = true;
    if (statusFilters.length > 0) {
      matchesStatus = statusFilters.some(filter => {
        if (filter === 'open') {
          return incident.status === INCIDENT_STATUS.OPEN;
        } else if (filter === 'closed') {
          return incident.status === INCIDENT_STATUS.CLOSED;
        }
        return false;
      });
    }

    return matchesSearch && matchesStatus;
  });

  // Check if user has access to incident management feature
  if (!hasFeature('incident_management')) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <div className="mb-4">
            <FiAlertCircle className="h-12 w-12 text-yellow-500 mx-auto" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Funcionalidad no disponible
          </h2>
          <p className="text-gray-600 mb-4">
            La gestión de incidencias no está disponible en su plan actual.
          </p>
          <p className="text-sm text-gray-500">
            Por favor, contacte con el administrador para actualizar su plan de suscripción.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Gestión de Incidencias</h1>
          <p className="mt-2 text-sm text-gray-600">
            Administra las incidencias reportadas en las habitaciones
          </p>
        </div>
        {canCreate() && (
          <button
            onClick={handleCreateIncident}
            className="mt-4 sm:mt-0 btn btn-primary flex items-center space-x-2"
          >
            <FiPlus className="h-4 w-4" />
            <span>Nueva Incidencia</span>
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative w-[200px]">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input pl-9 w-full text-sm"
            />
          </div>

          {/* Status filter buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => toggleStatusFilter('open')}
              className={`px-3 py-2 text-xs font-semibold rounded-full border transition-colors ${
                statusFilters.includes('open')
                  ? 'bg-red-100 text-red-800 border-red-200'
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Abiertas
            </button>
            <button
              onClick={() => toggleStatusFilter('closed')}
              className={`px-3 py-2 text-xs font-semibold rounded-full border transition-colors ${
                statusFilters.includes('closed')
                  ? 'bg-green-100 text-green-800 border-green-200'
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Cerradas
            </button>
          </div>
        </div>
      </div>

      {/* Content with Skeleton */}
      {isLoading ? (
        <SkeletonTable rows={8} columns={5} showActions />
      ) : (
        <IncidentTable
          incidents={filteredIncidents}
          onEdit={handleEditIncident}
          onDelete={handleDeleteIncident}
          onClose={handleCloseIncident}
          onReopen={handleReopenIncident}
          currentUser={user}
          loading={isLoading}
        />
      )}

      {/* Incident Form Modal */}
      {showIncidentForm && (
        <IncidentForm
          incident={selectedIncident}
          onSave={handleIncidentSaved}
          onClose={() => {
            setShowIncidentForm(false);
            setSelectedIncident(null);
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <ConfirmDialog
          title="Eliminar Incidencia"
          message={`¿Estás seguro de que deseas eliminar la incidencia de la habitación ${deleteConfirm.room_number}?`}
          confirmText="Eliminar"
          confirmStyle="danger"
          onConfirm={confirmDeleteIncident}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}
    </div>
  );
};

export default IncidentsManagement;